import type { HelpTriggerWeights } from '../HelpTriggerIcon/HelpTriggerIcon';

import { AnalyticsId, IconSize } from '../../constants';
import { useExpand } from '../../hooks/useExpand';
import { usePseudoClasses } from '../../hooks/usePseudoClasses';
import { getAriaLabelAttributes } from '../../utils/accessibility';
import { isMutableRefObject, mergeRefs } from '../../utils/refs';
import HelpDetails from '../HelpDetails/HelpDetails';
import HelpTriggerStandalone from '../HelpTriggerStandalone';
import Icon from '../Icon';
import ChevronDown from '../Icons/ChevronDown';
import ChevronUp from '../Icons/ChevronUp';

import styles from './styles.module.scss';

export interface HelpExpanderStandaloneProps {
  /** Sets aria-label of the trigger. */
  ariaLabel?: string;
  /** Sets aria-labelledby of the trigger. */
  ariaLabelledById?: string;
  /** Sets the text content of the help text that appears when expanded. */
  children: React.ReactNode;
  /** Expands the component */
  expanded?: boolean;
  /** Called when expander is open/closed. */
  onExpand?: (isExpanded: boolean) => void;
  /**
   * Sets the colors of the help trigger. Default: normal.
   */
  weight?: HelpTriggerWeights;
  /**
   * Classname will be applied to the button element.
   */
  className?: string;
  /** Sets the data-testid attribute. */
  testId?: string;
  /** The text that will be displayed in the trigger. */
  triggerText: string;
  /** Ref passed to the component */
  ref?: React.Ref<HTMLButtonElement | null>;
}

const HelpExpanderStandalone: React.FC<HelpExpanderStandaloneProps> = props => {
  const {
    ariaLabel,
    ariaLabelledById,
    children,
    className,
    expanded = false,
    onExpand,
    testId,
    triggerText,
    weight = 'normal',
    ref,
  } = props;
  const ariaLabelAttributes = getAriaLabelAttributes({ label: ariaLabel, id: ariaLabelledById });
  const [isExpanded, setIsExpanded] = useExpand(expanded, onExpand);
  const { refObject, isHovered } = usePseudoClasses<HTMLButtonElement>(isMutableRefObject(ref) ? ref : null);

  return (
    <div className={className} data-testid={testId} data-analyticsid={AnalyticsId.HelpExpanderStandalone}>
      <HelpTriggerStandalone
        className={styles['help-expander-standalone-trigger']}
        aria-expanded={isExpanded}
        onClick={(): void => setIsExpanded(!isExpanded)}
        ref={mergeRefs([ref, refObject])}
        weight={weight}
        {...ariaLabelAttributes}
      >
        {triggerText}
        <Icon
          color={`var(--core-color-plum-${isHovered ? '900' : '700'})`}
          svgIcon={isExpanded ? ChevronUp : ChevronDown}
          size={IconSize.XXSmall}
        />
      </HelpTriggerStandalone>
      {isExpanded && <HelpDetails>{children}</HelpDetails>}
    </div>
  );
};

HelpExpanderStandalone.displayName = 'HelpExpanderStandalone';

export default HelpExpanderStandalone;
